// The Team tasks' standard team of helpers (backend services/jobs/team.yaml:
// the Lead, the writing lead, the two checkers, the code reviewer, the
// general worker), as the notice that offers to install it says it
// (./TeamSetupNotice.tsx). Shown only where it matters: Team tasks are on,
// the team is not all there, and the workspace uses Team tasks (one was
// started here, or the Lead is installed: the jobs list's `team_used`) or
// the operator is asking for one (New Team task, the Team tasks list, a
// refusal for the missing team). Team tasks can be on only because app
// building needs the switch; such a workspace sees nothing. Pure, so
// ./jobModel.selftest.ts pins it.

import type { JobTeamResponse } from "@/api/jobs";
import { JOB_TEAM_TEMPLATES } from "@/features/subagents/purpose";
import { joinList, pluralize } from "@/lib/format";

/** The refusal of a Team task when the team is not installed (409 team_missing). The start dialog (now or on a
 *  schedule), the schedules lists, New Team task, a task's page and its reply show the notice (TeamSetupNotice,
 *  asked) beside it, so the install is one press away. */
export const TEAM_MISSING_TEXT = "Team tasks need their standard team of helpers, which isn't installed here yet.";

export interface TeamFacts {
  /** Team tasks are on (the jobs list's `offered`). */
  offered: boolean;
  /** The helpers the workspace lacks (`team_missing`). */
  missing: readonly string[];
  /** The workspace uses Team tasks (`team_used`). */
  used: boolean;
  /** The operator is asking for a Team task here. */
  asked: boolean;
}

export function teamNoticeShown(f: TeamFacts): boolean {
  return f.offered && f.missing.length > 0 && (f.used || f.asked);
}

/** The notice's one line, and its tooltip naming the helpers it would add. */
export function teamNoticeLine(missing: readonly string[], nameOf: (template: string) => string): { text: string; title: string } {
  const names = missing.map(nameOf);
  const all = missing.length >= JOB_TEAM_TEMPLATES.size;
  const text = all
    ? "Team tasks use a standard team of helpers. It isn't installed in this workspace yet."
    : `Team tasks use a standard team of helpers. ${missing.length === 1 ? `${names[0]} is` : `${joinList(names)} are`} missing here.`;
  return { text, title: `Installs ${joinList(names)}` };
}

/** "Install the team" / "Add the missing helper" / "Add the 2 missing helpers". */
export function teamButtonLabel(missing: readonly string[]): string {
  if (missing.length >= JOB_TEAM_TEMPLATES.size) return "Install the team";
  return missing.length === 1 ? "Add the missing helper" : `Add the ${pluralize(missing.length, "missing helper")}`;
}

/** What the install did: "The team is installed. Team tasks can start." / "Installed Reviewer. The team is complete." / "Could not install Job Lead: try again in a minute." */
export function teamInstalledLine(res: JobTeamResponse, nameOf: (template: string) => string): string {
  const created = res.created ?? [];
  const failed = Object.keys(res.failed ?? {});
  if (!failed.length) {
    if (!created.length) return "The team was already installed.";
    return created.length >= JOB_TEAM_TEMPLATES.size ? "The team is installed. Team tasks can start." : `Installed ${joinList(created.map(nameOf))}. The team is complete.`;
  }
  return [created.length ? `Installed ${joinList(created.map(nameOf))}.` : "", `Could not install ${joinList(failed.map(nameOf))}: try again in a minute.`].filter(Boolean).join(" ");
}

/** Why each one failed, in the server's words, for the line under it ("Job Lead: provision_failed: …"); "" when none did. */
export function teamInstallDetail(res: JobTeamResponse, nameOf: (template: string) => string): string {
  return Object.entries(res.failed ?? {})
    .map(([name, why]) => `${nameOf(name)}: ${why}`)
    .join("\n");
}
